import { useCallback, useEffect, useRef, useState } from 'react';
import SvgIcon from '../SvgIcon/SvgIcon';
import Text from '../Text/Text';
import { svgReferences } from '../../utils/svg/svg';
import './Banner.scss';

export type BannerType = 'success' | 'info' | 'alert' | 'error';

interface BannerProps {
  type: BannerType;
  message?: string;
  dismissible?: boolean;
  timeout?: number;
}

const typeIcons: Record<BannerType, keyof typeof svgReferences> = {
  success: 'CircleCheck',
  info: 'CircleInfo',
  alert: 'CircleExclamation',
  error: 'TriangleExclamation',
};

const typeLabels: Record<BannerType, string> = {
  success: 'Success',
  info: 'Information',
  alert: 'Alert',
  error: 'Error',
};

const FADE_DURATION_MS = 200;

const Banner = ({ type, message, dismissible = true, timeout }: BannerProps) => {
  const [visible, setVisible] = useState(true);
  const [dismissing, setDismissing] = useState(false);
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const dismiss = useCallback(() => {
    setDismissing(true);
    fadeTimerRef.current = setTimeout(() => setVisible(false), FADE_DURATION_MS);
  }, []);

  useEffect(() => {
    if (timeout === undefined) return;
    const id = setTimeout(dismiss, timeout);
    return () => clearTimeout(id);
  }, [timeout, dismiss]);

  useEffect(() => {
    return () => {
      if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
    };
  }, []);

  if (!visible) return null;

  const hasMessage = !!message;

  return (
    <div
      className={`banner banner-${type} ${hasMessage ? 'banner-large' : 'banner-small'}${
        dismissing ? ' banner-dismissing' : ''
      }`}
    >
      <div className="banner-stripe" />
      <div className="banner-content">
        <div className="banner-header">
          <SvgIcon iconName={typeIcons[type]} width={16} height={16} />
          <Text type="callout">{typeLabels[type]}</Text>
        </div>
        {hasMessage && (
          <Text type="body" className="banner-message">
            {message}
          </Text>
        )}
      </div>
      {dismissible && (
        <button
          type="button"
          className="banner-dismiss"
          aria-label={`Dismiss ${typeLabels[type]} banner`}
          onClick={dismiss}
        >
          <SvgIcon iconName="Xmark" width={12} height={12} />
        </button>
      )}
    </div>
  );
};

export default Banner;
